// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { type CredentialInfo, FactorType } from "@api/types";
import { useSession } from "@common/authorization/context/Session";
import AuthenticatorList from "@components/authenticatorList/AuthenticatorList";
import type { AuthenticatorCardProps } from "@components/authenticatorList/authenticatorCard/AuthenticatorCard";
import { Modal } from "@imprivata-cloud/components";
import { Spin } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ApplicationsIcon from "../../../../assets/icons/applications.svg?react";
import QuestionImg from "../../../../assets/icons/question.svg?react";
import mapFactorTypeToNameAndIcon from "../../../../common/authMethodInfo/authMethodInfo";
import getRelativeTime from "../../../../common/authMethodInfo/relativeTime";
import {
	useCredentialDelete,
	useUnenrollAuthenticator,
	useUserAuthenticators,
	useUserCredentials,
} from "../hooks/useAuthenticators";

import "./UserListRowMenu.less";
import { useErrorContext } from "@errorHandler/context/ErrorContext";
import { type ApiError, AppError } from "@errorHandler/errors";

export interface MenuButtonProps {
	userId: string;
}

const unableToRemoveAuthMethods = [FactorType.TEMP_CODE, FactorType.PASSWORD, FactorType.OTP];

const TRANSLATION = "portal.settings.users";

export const UserListRowMenu = ({ userId }: MenuButtonProps) => {
	const { tenantId } = useSession();
	const { emitError } = useErrorContext();
	const { t, i18n } = useTranslation();
	const [selectedAuthenticatorId, setSelectedAuthenticatorId] = useState<
		string | null
	>(null);
	const [selectedCredential, setSelectedCredential] =
		useState<CredentialInfo | null>(null);
	const [authnRemoveModalOpen, setAuthnRemoveModalOpen] = useState(false);
	const [credRemoveModalOpen, setCredRemoveModalOpen] = useState(false);
	const { data: authenticators, isFetching: isFetchingAuthenticators } =
		useUserAuthenticators(userId);
	const { data: credentialInfo, isFetching: isFetchingCredentials } =
		useUserCredentials(userId);
	const onError = (e: ApiError) => {
		emitError(new AppError(e));
	};
	const { mutate: unenrollAuthenticator } = useUnenrollAuthenticator(
		tenantId ?? "",
		{ onError },
	);
	const { mutate: deleteCredential } = useCredentialDelete({ onError });

	const isFetching = isFetchingAuthenticators || isFetchingCredentials;

	const authenticatorCards = useMemo((): AuthenticatorCardProps[] => {
		if (!authenticators) {
			return [];
		}
		return authenticators.map((auth) => {
			const { name, icon } = mapFactorTypeToNameAndIcon(auth.factorType, t);
			return {
				title: name,
				icon,
				subtitle:
					auth.factorType === FactorType.PASSWORD
						? ""
						: `${t("common.last-used")}${getRelativeTime(auth.lastUsedAt, i18n.language)}`,
				data: auth,
				enableRemove: !unableToRemoveAuthMethods.includes(auth.factorType),
				onRemove: () => {
					setSelectedAuthenticatorId(auth.authenticatorId);
					setAuthnRemoveModalOpen(true);
				},
			};
		});
	}, [authenticators, t, i18n.language]);

	const credentialCards = useMemo((): AuthenticatorCardProps[] => {
		if (!credentialInfo) {
			return [];
		}
		return credentialInfo.map((cred) => {
			return {
				title: cred.appDisplayName,
				icon: <ApplicationsIcon />,
				subtitle: cred.username,
				data: cred,
				onRemove: () => {
					setSelectedCredential(cred);
					setCredRemoveModalOpen(true);
				},
			};
		});
	}, [credentialInfo]);

	return (
		<>
			<Modal
				open={authnRemoveModalOpen}
				centered={true}
				onOk={() => {
					if (selectedAuthenticatorId) {
						unenrollAuthenticator(selectedAuthenticatorId);
						setAuthnRemoveModalOpen(false);
						setSelectedAuthenticatorId(null);
					}
				}}
				okText={t("common.remove")}
				onCancel={() => setAuthnRemoveModalOpen(false)}
				cancelText={t("common.dont-remove")}
				className="user-row-menu-modal"
				maskClosable={false}
			>
				<QuestionImg />
				<h3>{t(`${TRANSLATION}.authenticators.remove-title`)}</h3>
				<p>{t(`${TRANSLATION}.authenticators.remove-message`)}</p>
			</Modal>
			<Modal
				open={credRemoveModalOpen}
				centered={true}
				onOk={() => {
					if (selectedCredential) {
						deleteCredential(selectedCredential.credentialId);
						setCredRemoveModalOpen(false);
						setSelectedCredential(null);
					}
				}}
				okText={t("common.remove")}
				onCancel={() => setCredRemoveModalOpen(false)}
				cancelText={t("common.dont-remove")}
				className="user-row-menu-modal"
				maskClosable={false}
			>
				<QuestionImg />
				<h3>{t(`${TRANSLATION}.learned-creds.remove-title`)}</h3>
				<p>
					{t(`${TRANSLATION}.learned-creds.remove-message`, {
						userDisplayName: selectedCredential?.username,
						appName: selectedCredential?.appDisplayName,
					})}
				</p>
			</Modal>

			{isFetching ? (
				<div style={{ textAlign: "center", padding: "1rem" }}>
					<Spin data-testid={"loading-indicator"} size="small" />
				</div>
			) : (
				<>
					<AuthenticatorList
						title={t(`${TRANSLATION}.authenticators.title`)}
						cards={authenticatorCards}
						emptyMessage={t(`${TRANSLATION}.authenticators.empty`)}
					/>
					<AuthenticatorList
						title={t(`${TRANSLATION}.learned-creds.title`)}
						cards={credentialCards}
						emptyMessage={t(`${TRANSLATION}.learned-creds.empty`)}
					/>
				</>
			)}
		</>
	);
};

export default UserListRowMenu;
